import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import {
  Card,
  TextField,
  Select,
  Badge as PolarisBadge,
  Button as PolarisButton,
  Banner,
} from "@shopify/polaris";
import DataTable from "components/datatable/Datatable";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import PaginationControls from "components/pagination/PaginationControls";
import { ListFilter } from "lucide-react";
import { Column } from "types/Datatable";
import { PHONE_PAGE_GUTTER, PHONE_PAGE_MARGIN } from "components/tw/pageGutter";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
} from "components/modalx/modalx.slice";
import {
  fetchIncidents,
  IncidentListItem,
  IncidentSeverity,
  IncidentStatus,
  IncidentType,
} from "services/work-security-incidents.service";
import { WorkSecurityListPagination } from "services/work-security.service";
import { WorkSecurityArea, WorkSecurityAreaScopeProps } from "services/work-security-areas.service";

interface Props extends WorkSecurityAreaScopeProps {
  /** Accessible areas (from the module header's useAreaScope) — handed to the "Register Incident" modal (PR-2 §P4). */
  areas?: WorkSecurityArea[];
}

export function incidentTypeLabel(t: any, type: IncidentType) {
  return type === "event_at_work"
    ? t("incident_type_event_at_work", { defaultValue: "Event at work" })
    : t("incident_type_incident", { defaultValue: "Incident" });
}

export function incidentSeverityLabel(t: any, severity: IncidentSeverity) {
  switch (severity) {
    case "lengvas":
      return t("incident_severity_lengvas", { defaultValue: "Light" });
    case "sunkus":
      return t("incident_severity_sunkus", { defaultValue: "Serious" });
    case "mirtinas":
      return t("incident_severity_mirtinas", { defaultValue: "Fatal" });
    default:
      return "—";
  }
}

export function incidentStatusLabel(t: any, status: IncidentStatus) {
  switch (status) {
    case "registered":
      return t("incident_status_registered", { defaultValue: "Registered" });
    case "pending_signatures":
      return t("incident_status_pending_signatures", { defaultValue: "Pending signatures" });
    case "signed":
      return t("incident_status_signed", { defaultValue: "Signed" });
    case "closed":
      return t("incident_status_closed", { defaultValue: "Closed" });
    case "cancelled":
      return t("incident_status_cancelled", { defaultValue: "Cancelled" });
    default:
      return status;
  }
}

function statusTone(status: IncidentStatus): any {
  switch (status) {
    case "signed":
      return "success";
    case "pending_signatures":
      return "attention";
    case "closed":
      return "info";
    case "cancelled":
      return "critical";
    default:
      return undefined;
  }
}

function severityTone(severity: IncidentSeverity): any {
  switch (severity) {
    case "mirtinas":
      return "critical";
    case "sunkus":
      return "warning";
    case "lengvas":
      return "attention";
    default:
      return undefined;
  }
}

function formatDate(ms: number | null): string {
  if (!ms) return "—";
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
}

/**
 * Incident register (the legal journal view). Embedded as a tab inside the Work
 * Security page — no <Page> wrapper. Renders its own "Register Incident" action.
 *
 * `areaId` follows the PR-2 §P1 hard rule (see useAreaScope): empty when
 * there is nothing to disambiguate, so the fetch call below is unaffected
 * for the common single-area company. No "Sritis" column here — verified
 * against IncidentService.serializeListItem, the list payload does not carry
 * a per-row area (see the NOTE on IncidentListItem in the service file).
 */
export default function WorkSecurityIncidentsPanel({
  areaId,
  selectedArea,
  areas = [],
}: Props = {}) {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const [incidents, setIncidents] = useState<IncidentListItem[]>([]);
  const [pagination, setPagination] = useState<WorkSecurityListPagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [textSearch, setTextSearch] = useState("");
  const [debouncedTextSearch, setDebouncedTextSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<IncidentStatus | "">("");
  const [typeFilter, setTypeFilter] = useState<IncidentType | "">("");
  /** "" = active only (API default); "only" = archived; "all" = both */
  const [archivedFilter, setArchivedFilter] = useState<"" | "only" | "all">("");
  const [paginationState, setPaginationState] = useState({ page: 1, pageSize: 20 });
  const requestRef = useRef(0);

  // On a phone the four filters would fill the first screen, so they fold behind one button
  // that says how many of them are set.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilterCount = [textSearch.trim(), typeFilter, statusFilter, archivedFilter].filter(
    Boolean
  ).length;

  const { page, pageSize } = paginationState;
  const pageSizeOptions = [10, 20, 50, 100];

  // Switching spheres resets pagination the same way switching companies
  // does (see work-security.tsx's own roles-tab reset effect, PR-2 §P1).
  useEffect(() => {
    setPaginationState({ page: 1, pageSize: 20 });
  }, [areaId]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedTextSearch((current) => {
        if (current === textSearch) return current;
        setPaginationState((prev) => ({ ...prev, page: 1 }));
        return textSearch;
      });
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [textSearch]);

  const loadIncidents = useCallback(async () => {
    const requestId = ++requestRef.current;
    if (!companyId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await fetchIncidents(companyId, {
        page,
        pageSize,
        filtersValue: {
          textSearch: debouncedTextSearch,
          status: statusFilter,
          type: typeFilter,
          ...(areaId ? { areaId } : {}),
        },
        ...(archivedFilter ? { archived: archivedFilter } : {}),
      });
      if (requestId !== requestRef.current) return;
      setLoadError(false);
      setIncidents(data.entries || []);
      setPagination(data.pagination || null);
    } catch (err) {
      if (requestId !== requestRef.current) return;
      console.error("Failed to load incidents", err);
      setLoadError(true);
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [
    companyId,
    page,
    pageSize,
    debouncedTextSearch,
    statusFilter,
    typeFilter,
    archivedFilter,
    areaId,
  ]);

  useEffect(() => {
    loadIncidents();
  }, [loadIncidents]);

  const handleRegisterClick = () => {
    dispatch(
      setModalxOptions({
        title: t("register_incident", { defaultValue: "Register Incident" }),
      })
    );
    dispatch(setModalxComponent("IncidentFormModal"));
    dispatch(
      setModalxComponentProps({
        // PR-2 §P4: only spheres that track incidents (`hasIncidents`) are
        // ever a valid destination for a new one — offering the rest would
        // just be a guaranteed AREA_INCIDENTS_DISABLED round-trip.
        areas: areas.filter((area) => area.hasIncidents),
        selectedArea,
        onSuccess: (incidentId?: string) => {
          if (incidentId) {
            navigate(`/app/${companyId}/work-security/incidents/${incidentId}`);
          } else {
            loadIncidents();
          }
        },
      })
    );
  };

  const collection = incidents.map((i) => ({
    ...i,
    viewUrl: `/app/${companyId}/work-security/incidents/${i.id}`,
  }));

  const totalItems = typeof pagination?.total === "number" ? pagination.total : incidents.length;
  const totalPages =
    typeof pagination?.totalPages === "number"
      ? pagination.totalPages
      : totalItems > 0
        ? Math.ceil(totalItems / pageSize)
        : 0;

  const columns: Column[] = [
    { key: "number", name: t("incident_number", { defaultValue: "No." }) },
    {
      key: "occurredAt",
      name: t("incident_occurred_at", { defaultValue: "Date" }),
      type: "custom",
      mobileView: "bottomLeft",
      render: (item: IncidentListItem) => <span className="whitespace-nowrap">{formatDate(item.occurredAt)}</span>,
    },
    {
      key: "type",
      name: t("incident_type", { defaultValue: "Type" }),
      type: "custom",
      mobileView: "bottomRight",
      render: (item: IncidentListItem) => (
        <div className="flex flex-col gap-1">
          <PolarisBadge>{incidentTypeLabel(t, item.type)}</PolarisBadge>
          {item.severity !== "none" && (
            <PolarisBadge tone={severityTone(item.severity)}>
              {incidentSeverityLabel(t, item.severity)}
            </PolarisBadge>
          )}
        </div>
      ),
    },
    {
      key: "victim",
      name: t("incident_victim", { defaultValue: "Affected employee" }),
      type: "custom",
      mobileView: "topLeft",
      render: (item: IncidentListItem) => (
        <div>
          <p className="font-semibold text-zinc-900 dark:text-white">{item.victimName || "—"}</p>
          {item.victimPosition && <p className="text-xs text-zinc-500">{item.victimPosition}</p>}
        </div>
      ),
    },
    {
      key: "location",
      name: t("incident_location", { defaultValue: "Location" }),
      type: "custom",
      render: (item: IncidentListItem) => (
        <span className="text-sm text-zinc-700 dark:text-zinc-300">{item.location || "—"}</span>
      ),
    },
    {
      key: "status",
      name: t("status", { defaultValue: "Status" }),
      type: "custom",
      mobileView: "topRight",
      render: (item: IncidentListItem) => (
        <div className="flex flex-col gap-1">
          <PolarisBadge tone={statusTone(item.status)}>
            {incidentStatusLabel(t, item.status)}
          </PolarisBadge>
          {item.isArchived && (
            <PolarisBadge>{t("status_archived", { defaultValue: "Archived" })}</PolarisBadge>
          )}
          {item.deadline?.isOverdue &&
            item.severity !== "sunkus" &&
            item.severity !== "mirtinas" &&
            !["signed", "closed", "cancelled"].includes(item.status) && (
            <PolarisBadge tone="critical">
              {t("incident_deadline_overdue", { defaultValue: "Investigation overdue" })}
            </PolarisBadge>
          )}
        </div>
      ),
    },
    {
      key: "actions",
      name: "",
      type: "custom",
      render: (item: any) => (
        <div className="text-right">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(item.viewUrl);
            }}
            className="text-blue-600 hover:text-blue-800 font-semibold bg-transparent border-0 cursor-pointer max-md:min-h-11"
          >
            {t("view", { defaultValue: "View" })} &rarr;
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4 mt-4">
      <div className={`flex justify-end ${PHONE_PAGE_GUTTER}`}>
        <PolarisButton variant="primary" onClick={handleRegisterClick}>
          {t("register_incident", { defaultValue: "Register Incident" })}
        </PolarisButton>
      </div>

      {loadError && (
        <Banner tone="critical" onDismiss={() => setLoadError(false)}>
          {t("incidents_load_failed", {
            defaultValue: "Unable to load incidents. Please try again.",
          })}
        </Banner>
      )}

      <div className={`md:hidden ${PHONE_PAGE_GUTTER}`}>
        <button
          type="button"
          className="inline-flex min-h-11 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900"
          aria-expanded={filtersOpen}
          aria-controls="incident-filters"
          onClick={() => setFiltersOpen((open) => !open)}
        >
          <ListFilter className="h-4 w-4 text-gray-500" aria-hidden="true" />
          {t("ws_filters_toggle", { defaultValue: "Filters" })}
          {activeFilterCount > 0 && <span className="font-semibold">({activeFilterCount})</span>}
        </button>
      </div>

      <div id="incident-filters" className={filtersOpen ? undefined : "max-md:hidden"}>
        <Card>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 p-2 items-end">
            <div className="md:col-span-3 lg:col-span-2">
              <TextField
                label={t("search_incidents", { defaultValue: "Search Incidents" })}
                value={textSearch}
                onChange={setTextSearch}
                placeholder={t("search_incidents_placeholder", {
                  defaultValue: "Search by number, employee, location...",
                })}
                autoComplete="off"
                clearButton
                onClearButtonClick={() => setTextSearch("")}
              />
            </div>
            <div>
              <Select
                label={t("filter_by_type", { defaultValue: "Type" })}
                options={[
                  { label: t("all", { defaultValue: "All" }), value: "" },
                  { label: incidentTypeLabel(t, "incident"), value: "incident" },
                  { label: incidentTypeLabel(t, "event_at_work"), value: "event_at_work" },
                ]}
                value={typeFilter}
                onChange={(v) => {
                  setTypeFilter(v as IncidentType | "");
                  setPaginationState((prev) => ({ ...prev, page: 1 }));
                }}
              />
            </div>
            <div>
              <Select
                label={t("filter_by_status", { defaultValue: "Status" })}
                options={[
                  { label: t("all", { defaultValue: "All" }), value: "" },
                  { label: incidentStatusLabel(t, "registered"), value: "registered" },
                  {
                    label: incidentStatusLabel(t, "pending_signatures"),
                    value: "pending_signatures",
                  },
                  { label: incidentStatusLabel(t, "signed"), value: "signed" },
                  { label: incidentStatusLabel(t, "closed"), value: "closed" },
                  { label: incidentStatusLabel(t, "cancelled"), value: "cancelled" },
                ]}
                value={statusFilter}
                onChange={(v) => {
                  setStatusFilter(v as IncidentStatus | "");
                  setPaginationState((prev) => ({ ...prev, page: 1 }));
                }}
              />
            </div>
            <div>
              <Select
                label={t("filter_by_archived", { defaultValue: "Archived" })}
                options={[
                  { label: t("active", { defaultValue: "Active" }), value: "" },
                  {
                    label: t("archived_only", { defaultValue: "Archived only" }),
                    value: "only",
                  },
                  { label: t("all", { defaultValue: "All" }), value: "all" },
                ]}
                value={archivedFilter}
                onChange={(v) => {
                  setArchivedFilter(v as "" | "only" | "all");
                  setPaginationState((prev) => ({ ...prev, page: 1 }));
                }}
              />
            </div>
          </div>
        </Card>
      </div>

      <div className={`bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden dark:bg-zinc-900 dark:border-zinc-800 ${PHONE_PAGE_MARGIN}`}>
        {loading && incidents.length === 0 ? (
          <DataTableSkeleton rows={8} columns={columns.length} />
        ) : incidents.length === 0 ? (
          <div className="text-center py-16 px-4">
            <h3 className="mt-4 text-sm font-semibold text-zinc-900 dark:text-white">
              {Boolean(debouncedTextSearch || statusFilter || typeFilter || archivedFilter)
                ? t("no_incidents_match_filters", {
                    defaultValue: "No incidents match your filters",
                  })
                : t("no_incidents", { defaultValue: "No incidents registered" })}
            </h3>
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              {Boolean(debouncedTextSearch || statusFilter || typeFilter || archivedFilter)
                ? t("no_incidents_match_filters_description", {
                    defaultValue: "Try adjusting or clearing the filters.",
                  })
                : t("no_incidents_description", {
                    defaultValue: "Register a workplace incident to start the record.",
                  })}
            </p>
          </div>
        ) : (
          <>
            <DataTable
              collection={collection}
              rowUrl={(item: any) => item.viewUrl}
              columns={columns}
            />
            {pagination && totalItems > 0 && (
              <div className="border-t border-zinc-200 px-4 py-3 dark:border-zinc-800">
                <PaginationControls
                  page={page}
                  pageSize={pageSize}
                  total={totalItems}
                  totalPages={totalPages}
                  hasNextPage={totalPages > 0 ? page < totalPages : false}
                  hasPreviousPage={page > 1}
                  onPageChange={(nextPage: number) => {
                    if (nextPage > 0 && nextPage !== page) {
                      setPaginationState((prev) => ({ ...prev, page: nextPage }));
                    }
                  }}
                  onPageSizeChange={(nextPageSize: number) => {
                    if (nextPageSize > 0 && nextPageSize !== pageSize) {
                      setPaginationState({ page: 1, pageSize: nextPageSize });
                    }
                  }}
                  pageSizeOptions={pageSizeOptions}
                  isLoading={loading}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
